import { readAutheClaims, tenantContextFromClaims, tenancyNamesFromClaims, loadPermissionsView, switchTenant, switchOrganization } from '../chunk-5DXGEX7X.js';
import '../chunk-QY7F67KZ.js';
import '../chunk-FU73M74F.js';
import '../chunk-INX63VAT.js';
import '../chunk-SOT3IY2C.js';
import { storageKey, removeLocalKey, writeLocalJson, readLocalJson } from '../chunk-YI77I2GW.js';
import '../chunk-BXJJLTOH.js';
import '../chunk-645XZPZL.js';
import '../chunk-RN57KPDG.js';
import '../chunk-RMLZ5HCT.js';
import '../chunk-BRBMP7SU.js';
import '../chunk-MZH5OJVQ.js';
import '../chunk-WKJCXPB2.js';
import '../chunk-3RVTMGBC.js';
import '../chunk-N5LV7XVJ.js';
import '../chunk-BFUJOKOR.js';
import '../chunk-6B5KO6TH.js';
import '../chunk-3QN2VZ56.js';
import { decodeJwtPayload, createClaimReader } from '../chunk-LNDNXOJP.js';
import { singleflight } from '../chunk-LVCDLPYA.js';
import '../chunk-UEXNJL5R.js';
import { parseQaOverride, QA_OVERRIDE_PARAMS } from '../chunk-5WIUKZF6.js';
import '../chunk-BIXNZFFW.js';
import '../chunk-T57KORBT.js';
import { captureReturnTo, resolveLoginUrl, isAllowedReturnTo, readReturnTo } from '../chunk-O2TUCS6S.js';
import '../chunk-5FX2D4G5.js';
import '../chunk-AEYXO6NC.js';
import '../chunk-OBCJH3GP.js';
import { createLogger } from '../chunk-GPJ572YS.js';
import { brandClass } from '../chunk-N7SZ66QO.js';
import '../chunk-NCULDWVZ.js';
import '../chunk-LUSQNFVS.js';
import '../chunk-AHISJLEK.js';
import '../chunk-QXYP3KHC.js';
import '../chunk-M7KP2PIN.js';
import '../chunk-AHW5HTBY.js';
import '../chunk-PZ5AY32C.js';

// src/session/errors.ts
var SessionError = class extends Error {
  static {
    brandClass(this, "session/SessionError");
  }
  reason;
  constructor(reason, message, options) {
    super(message);
    this.name = "SessionError";
    this.reason = reason;
    if (options?.cause !== void 0) {
      this.cause = options.cause;
    }
  }
};
function readField(error, key) {
  if (error && typeof error === "object" && key in error) {
    const value = error[key];
    if (typeof value === "string") return value;
  }
  return "";
}
function classifyAuthError(error) {
  const code = readField(error, "error").toLowerCase();
  if (code === "invalid_grant" || code === "missing_refresh_token") return "session_invalid";
  if (code === "login_required" || code === "consent_required") return null;
  const text = `${readField(error, "error_description")} ${readField(error, "message")}`.toLowerCase();
  if (text.includes("refresh token")) return "session_invalid";
  return null;
}
function parseDenyReason(description) {
  const match = /^\s*\[([a-z_]+)\]\s*([\s\S]*)$/i.exec(description);
  const code = match?.[1]?.toLowerCase();
  const message = (match?.[2] || description || "").trim();
  if (code === "org_removed" || code === "organization_missing") {
    return { reason: "org_removed", message };
  }
  if (/organization no longer exists/i.test(description)) {
    return { reason: "org_removed", message: message || description };
  }
  return { reason: "access_denied", message: message || description };
}

// src/session/circuit-breaker.ts
var DEFAULT_KEY = "authe_auth_attempts";
var DEFAULT_MAX = 3;
var DEFAULT_WINDOW_MS = 3e4;
var AuthCircuitBreaker = class {
  static {
    brandClass(this, "session/AuthCircuitBreaker");
  }
  key;
  maxAttempts;
  windowMs;
  getStorage;
  now;
  constructor(options = {}) {
    this.key = options.key ?? DEFAULT_KEY;
    this.maxAttempts = options.maxAttempts ?? DEFAULT_MAX;
    this.windowMs = options.windowMs ?? DEFAULT_WINDOW_MS;
    this.getStorage = options.getStorage ?? (() => typeof localStorage !== "undefined" ? localStorage : void 0);
    this.now = options.now ?? (() => Date.now());
  }
  /** Redirect attempts still inside the sliding window. */
  attemptsInWindow() {
    return this.readFresh().length;
  }
  /** `true` while we're under the attempt budget — check before redirecting. */
  canAttempt() {
    return this.attemptsInWindow() < this.maxAttempts;
  }
  /** `true` once the budget is exhausted (the loop has been broken). */
  isOpen() {
    return !this.canAttempt();
  }
  /** Record that we're about to redirect to authenticate. */
  recordAttempt() {
    const fresh = this.readFresh();
    fresh.push(this.now());
    this.write(fresh);
  }
  /** Clear the trip — call as soon as a valid session is established. */
  recordSuccess() {
    this.reset();
  }
  reset() {
    this.getStorage()?.removeItem(this.key);
  }
  readFresh() {
    const storage = this.getStorage();
    if (!storage) return [];
    let parsed;
    try {
      parsed = JSON.parse(storage.getItem(this.key) ?? "[]");
    } catch {
      return [];
    }
    if (!Array.isArray(parsed)) return [];
    const cutoff = this.now() - this.windowMs;
    return parsed.filter((t) => typeof t === "number" && t >= cutoff);
  }
  write(timestamps) {
    this.getStorage()?.setItem(this.key, JSON.stringify(timestamps));
  }
};

// src/session/cache.ts
var AUTH0_CACHE_KEY_PATTERNS = ["auth0spajs", "@@auth0"];
function matchesAuth0Cache(key) {
  return key.includes(AUTH0_CACHE_KEY_PATTERNS[0]) || key.startsWith(AUTH0_CACHE_KEY_PATTERNS[1]);
}
function clearAuthCache(storage) {
  const store = storage ?? (typeof localStorage !== "undefined" ? localStorage : void 0);
  if (!store) return 0;
  let removed = 0;
  try {
    for (const key of Object.keys(store)) {
      if (matchesAuth0Cache(key)) {
        store.removeItem(key);
        removed += 1;
      }
    }
  } catch {
  }
  return removed;
}

// src/session/handoff.ts
var SESSION_USER_PARAM = "authe_session_user";
function validSessionSubject(value) {
  const unsafe = /[\s\u0000-\u001f]/;
  return typeof value === "string" && value.length > 0 && value.length <= 256 && !unsafe.test(value);
}
function withSessionHandoff(destination, subject) {
  if (!validSessionSubject(subject)) throw new Error("Your session is not ready. Please sign in again.");
  const url = new URL(destination, typeof window === "undefined" ? void 0 : window.location.origin);
  url.searchParams.set(SESSION_USER_PARAM, subject);
  return url.href;
}

// src/session/qa-override.ts
var QA_OVERRIDE_STORAGE_KEY = storageKey("qa", "override");
var localQaOverrideStore = {
  read: () => {
    const value = readLocalJson(QA_OVERRIDE_STORAGE_KEY, null);
    return typeof value === "string" && parseQaOverride(value).kind === "set" ? value : null;
  },
  write: (value) => {
    writeLocalJson(QA_OVERRIDE_STORAGE_KEY, value);
  },
  clear: () => removeLocalKey(QA_OVERRIDE_STORAGE_KEY)
};
function readQaOverride(store = localQaOverrideStore) {
  return store.read();
}
function qaOverrideFromUrl(href) {
  let url;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const name = QA_OVERRIDE_PARAMS.find((param) => url.searchParams.has(param));
  if (!name) return null;
  const request = parseQaOverride(url.searchParams.get(name) ?? "");
  for (const param of QA_OVERRIDE_PARAMS) url.searchParams.delete(param);
  return { request, cleaned: url.pathname + url.search + url.hash };
}
function withQa(params, qa) {
  if (!qa) return params;
  return { ...params ?? {}, qa };
}
function withQaOverride(client, store) {
  return {
    isAuthenticated: () => client.isAuthenticated(),
    getUser: (() => client.getUser()),
    getIdTokenClaims: () => client.getIdTokenClaims(),
    getTokenSilently: (options) => client.getTokenSilently({
      ...options ?? {},
      authorizationParams: withQa(options?.authorizationParams, store.read())
    }),
    loginWithRedirect: (args) => client.loginWithRedirect({
      ...args ?? {},
      authorizationParams: withQa(args?.authorizationParams, store.read())
    }),
    handleRedirectCallback: ((url) => client.handleRedirectCallback(url)),
    logout: (args) => client.logout(args)
  };
}

// src/session/manager.ts
var DEFAULT_CLAIMS_NAMESPACE = "https://authe.io/";
var CALLBACK_PARAMS = ["code", "state", "error", "error_description"];
function deriveClaimsNamespace(urlContext, getLocation) {
  if (urlContext?.local) {
    const origin = getLocation()?.origin;
    if (origin) return `${origin}/`;
  }
  return urlContext?.rootDomain ? `https://${urlContext.rootDomain}/` : DEFAULT_CLAIMS_NAMESPACE;
}
function hasAuthCallbackParams(search) {
  const params = new URLSearchParams(search);
  return params.has("code") && params.has("state") || params.has("error");
}
function projectUser(idClaims, userId) {
  return {
    id: userId ?? (typeof idClaims.sub === "string" ? idClaims.sub : null),
    name: (typeof idClaims.name === "string" ? idClaims.name : null) ?? (typeof idClaims.nickname === "string" ? idClaims.nickname : null) ?? userId,
    email: typeof idClaims.email === "string" ? idClaims.email : null,
    avatar: typeof idClaims.picture === "string" ? idClaims.picture : null
  };
}
var SessionManager = class {
  static {
    brandClass(this, "session/SessionManager");
  }
  client;
  strategy;
  urlContext;
  restoreDeepLink;
  ns;
  policySource;
  breaker;
  log;
  getLocation;
  assign;
  replaceState;
  getRemintStore;
  identityListeners = /* @__PURE__ */ new Set();
  onIdentityChange;
  qaStore;
  /**
   * Mismatches already re-minted for; the loop guard in {@link reconcilePlan}.
   *
   * Mirrored into `sessionStorage` rather than kept only here, because a re-mint
   * RELOADS: by the time the same question is asked again this set is a fresh
   * empty one, which is precisely when a token that never converges turns one
   * re-mint into an endless reload. Per tab and discarded with it, which is the
   * lifetime "once per session" means. In-memory stays the first check so the
   * guard still works with storage unavailable or blocked.
   */
  remintedFor = /* @__PURE__ */ new Set();
  /** Collapses concurrent bootstraps (StrictMode double-mount) onto one call. */
  inflight = singleflight();
  /** Same coalescing for the login-site `resolvePostLogin` callback path. */
  inflightResolve = singleflight();
  handoffRedirectPending = false;
  constructor(options) {
    this.qaStore = options.qaOverride === void 0 ? localQaOverrideStore : options.qaOverride;
    this.client = this.qaStore ? withQaOverride(options.client, this.qaStore) : options.client;
    this.strategy = options.loginStrategy ?? "spa-redirect";
    this.urlContext = options.urlContext;
    this.restoreDeepLink = options.restoreDeepLink ?? false;
    this.getLocation = options.getLocation ?? (() => typeof window !== "undefined" ? window.location : void 0);
    this.ns = options.claimsNamespace ?? deriveClaimsNamespace(options.urlContext, this.getLocation);
    this.policySource = options.policySource ?? null;
    this.breaker = options.breaker ?? new AuthCircuitBreaker();
    this.log = options.logger ?? createLogger("session");
    this.onIdentityChange = options.onIdentityChange;
    this.assign = options.assign ?? ((url) => {
      if (typeof window !== "undefined") window.location.assign(url);
    });
    this.replaceState = options.replaceState ?? ((url) => {
      if (typeof history !== "undefined") history.replaceState(null, "", url);
    });
    this.getRemintStore = options.getRemintStore ?? (() => {
      try {
        const s = typeof sessionStorage !== "undefined" ? sessionStorage : void 0;
        return s && typeof s.getItem === "function" ? s : void 0;
      } catch {
        return void 0;
      }
    });
  }
  /**
   * Resolve the session on app load. Handles an in-flight Auth0 callback (code
   * exchange + URL cleanup), otherwise restores from the SDK cache / refresh
   * token. A live session resets the loop breaker.
   *
   * De-duplicated: React 18 StrictMode (and any double-mount) fires the
   * bootstrapping effect twice, and the OAuth `code` is single-use — a second
   * concurrent `handleRedirectCallback` would reject with "invalid state". The
   * singleflight collapses overlapping calls onto ONE bootstrap so the code is
   * exchanged exactly once; the entry clears on settle so a later load re-runs.
   */
  async bootstrap() {
    return this.inflight.run("bootstrap", () => this.runBootstrap());
  }
  async runBootstrap() {
    if (this.handoffRedirectPending) return { status: "unknown", session: null };
    const loc = this.getLocation();
    try {
      if (loc && hasAuthCallbackParams(loc.search)) {
        return await this.completeCallback(loc);
      }
      await this.applyQaOverrideFromUrl();
      const current = this.getLocation();
      if (current && new URLSearchParams(current.search).has(SESSION_USER_PARAM)) {
        const target = new URL(current.href);
        if (target.searchParams.has(SESSION_USER_PARAM)) {
          const sessionUser = target.searchParams.get(SESSION_USER_PARAM);
          target.searchParams.delete(SESSION_USER_PARAM);
          this.replaceState(target.pathname + target.search + target.hash);
          if (!validSessionSubject(sessionUser)) throw new Error("Invalid sign-in handoff. Please sign in again.");
          this.handoffRedirectPending = true;
          await this.tearDownIdentity("logout");
          clearAuthCache();
          await this.client.loginWithRedirect({ appState: { returnTo: target.href, sessionUser } });
          return { status: "unknown", session: null };
        }
      }
      return await this.toState(await this.loadSession());
    } catch (error) {
      this.handoffRedirectPending = false;
      this.log.warn("bootstrap failed", { error });
      return { status: "error", session: null, error };
    }
  }
  async completeCallback(loc) {
    const params = new URLSearchParams(loc.search);
    if (params.has("error")) {
      const description = params.get("error_description") || params.get("error") || "auth error";
      const { reason, message } = parseDenyReason(description);
      const err = new SessionError(reason, message);
      this.cleanUrl(loc);
      this.log.warn("auth callback returned an error", { error: params.get("error"), reason });
      return { status: "error", session: null, error: err };
    }
    let returnTo;
    let sessionUser;
    try {
      const result = await this.client.handleRedirectCallback(loc.href);
      returnTo = result.appState?.returnTo;
      sessionUser = result.appState?.sessionUser;
      if (returnTo) this.log.debug("auth callback returnTo", { returnTo });
    } catch (error) {
      this.cleanUrl(loc);
      clearAuthCache();
      throw error;
    }
    this.finishCallbackUrl(returnTo, loc);
    if (sessionUser !== void 0 && (!validSessionSubject(sessionUser) || (await this.client.getIdTokenClaims())?.sub !== sessionUser)) {
      clearAuthCache();
      throw new SessionError("access_denied", "Your account changed while signing in. Please sign in again.");
    }
    return this.toState(await this.loadSession());
  }
  /**
   * Tidy the URL after a callback: when {@link restoreDeepLink} is on and the
   * captured `returnTo` is same-origin, replace it with that deep link (path +
   * query + hash) so a destination SPA lands back where the user started;
   * otherwise just strip the `?code&state` callback params in place.
   */
  finishCallbackUrl(returnTo, loc) {
    if (this.restoreDeepLink && returnTo) {
      try {
        const target = new URL(returnTo, loc.origin);
        if (target.origin === loc.origin) {
          this.replaceState(target.pathname + target.search + target.hash);
          return;
        }
        this.log.debug("ignoring cross-origin returnTo after callback", { returnTo });
      } catch {
      }
    }
    this.cleanUrl(loc);
  }
  /**
   * Adopt a `?qa=` (or `?colour=`) routing override from the URL: store it (or
   * drop it for `clear`), strip the param, and re-mint the token so the claim —
   * which is what actually routes requests, in the browser and the backend —
   * reflects it before anything reads the session. Unauthenticated, the stored
   * value rides on the login redirect instead.
   */
  async applyQaOverrideFromUrl() {
    const loc = this.getLocation();
    if (!loc) return;
    const found = qaOverrideFromUrl(loc.href);
    if (!found) return;
    this.replaceState(found.cleaned);
    if (!this.qaStore) return;
    const { request } = found;
    if (request.kind === "invalid") {
      this.log.warn("ignoring an invalid ?qa= routing override", { value: request.raw });
      return;
    }
    if (request.kind === "clear") this.qaStore.clear();
    else this.qaStore.write(request.value);
    this.log.info("qa routing override", { override: this.qaStore.read() ?? "cleared" });
    if (!await this.client.isAuthenticated()) return;
    try {
      await this.client.getTokenSilently({ cacheMode: "off" });
    } catch (error) {
      this.log.warn("could not re-mint the session for the qa override", { error });
    }
  }
  /** The `?qa=` routing override this session sends, or `null` (persistent routing). */
  getQaOverride() {
    return this.qaStore?.read() ?? null;
  }
  /** Load + project the current session, or `null` when unauthenticated. */
  async loadSession() {
    if (!await this.client.isAuthenticated()) return null;
    try {
      const accessToken = await this.client.getTokenSilently();
      const idClaims = await this.client.getIdTokenClaims() ?? {};
      const claims = readAutheClaims(idClaims, this.ns);
      const tenant = tenantContextFromClaims(idClaims, this.ns);
      const names = tenancyNamesFromClaims(idClaims, this.ns);
      const permissions = await loadPermissionsView({
        roles: claims.roles,
        accessToken,
        namespace: this.ns,
        policySource: this.policySource
      });
      if (permissions.scopes.length) claims.scopes = permissions.scopes;
      if (Object.keys(permissions.rolePermissions).length) {
        claims.role_permissions = permissions.rolePermissions;
      }
      return {
        accessToken,
        idClaims,
        claims,
        tenant,
        names,
        permissions,
        user: projectUser(idClaims, claims.user_id ?? null)
      };
    } catch (error) {
      this.log.warn("loadSession failed despite isAuthenticated", { error });
      if (classifyAuthError(error) === "session_invalid") clearAuthCache();
      return null;
    }
  }
  /** Current access token (refreshed by the SDK if needed), or `null`. */
  async getAccessToken() {
    try {
      return await this.client.getTokenSilently();
    } catch (error) {
      if (classifyAuthError(error) === "session_invalid") clearAuthCache();
      return null;
    }
  }
  /**
   * Force a fresh access token from Auth0, bypassing the SDK cache
   * (`cacheMode: "off"`). Use when the gateway rejects the *cached* token as
   * expired/invalid (clock skew, a rotated signing key, or the SDK's expiry
   * estimate lagging the server) so the SDK re-mints off the refresh token and
   * updates its cache — subsequent {@link getAccessToken} then return the fresh
   * token. Returns the new token, or `null` when refresh isn't possible (the
   * caller should fall back to {@link login}).
   */
  async refreshAccessToken() {
    try {
      return await this.client.getTokenSilently({ cacheMode: "off" });
    } catch (error) {
      this.log.warn("forced token refresh failed", { error });
      if (classifyAuthError(error) === "session_invalid") clearAuthCache();
      return null;
    }
  }
  async isAuthenticated() {
    return this.client.isAuthenticated();
  }
  /**
   * Return the live session or kick off a login redirect — the guard a route
   * uses. When the loop breaker is open it returns `blocked` instead of
   * redirecting again, so the app can render an error rather than thrash.
   */
  async requireSession(options = {}) {
    const session = await this.loadSession();
    if (session) {
      this.breaker.recordSuccess();
      return { outcome: "authenticated", session };
    }
    if (!this.breaker.canAttempt()) {
      const error = new Error(
        `Authentication loop detected (${this.breaker.attemptsInWindow()} redirects). Stopped retrying.`
      );
      this.log.error("auth loop broken \u2014 not redirecting", { attempts: this.breaker.attemptsInWindow() });
      return { outcome: "blocked", error };
    }
    this.breaker.recordAttempt();
    await this.login(options);
    return { outcome: "redirecting" };
  }
  /** Begin authentication per the configured strategy. */
  async login(options = {}) {
    if (options.identityChange) await this.tearDownIdentity(options.identityChange);
    const returnTo = options.returnTo ?? captureReturnTo(this.getLocation());
    if (this.strategy === "external-site") {
      if (!this.urlContext) {
        throw new Error("SessionManager: external-site login needs a urlContext");
      }
      const qa = this.getQaOverride() ?? void 0;
      const url = resolveLoginUrl({ returnTo, params: { ...options.params, ...qa ? { qa } : {} } }, this.urlContext);
      this.log.debug("redirecting to login site", { url });
      this.assign(url);
      return;
    }
    this.log.debug("loginWithRedirect", { returnTo });
    await this.client.loginWithRedirect({
      appState: { returnTo },
      authorizationParams: stripUndefined(options.params)
    });
  }
  /**
   * Complete an Auth0 callback ON THE LOGIN SITE and return the validated
   * post-login destination to navigate to (appState wins, else the `returnTo`
   * query param, else the fallback). Cleans the callback params from the URL.
   *
   * De-duplicated exactly like {@link bootstrap}: the login site's callback
   * screen fires its effect twice under React StrictMode (and any double-mount),
   * and the OAuth `code`/PKCE transaction is single-use — a second concurrent
   * `handleRedirectCallback` rejects with "missing_transaction"/"Invalid state".
   * The singleflight collapses overlapping calls onto ONE exchange; the entry
   * clears on settle so a later navigation re-runs cleanly.
   */
  async resolvePostLogin(fallback) {
    return this.inflightResolve.run("resolvePostLogin", () => this.runResolvePostLogin(fallback));
  }
  async runResolvePostLogin(fallback) {
    const loc = this.getLocation();
    let appStateReturnTo;
    if (loc && hasAuthCallbackParams(loc.search) && !new URLSearchParams(loc.search).has("error")) {
      try {
        const result = await this.client.handleRedirectCallback(loc.href);
        appStateReturnTo = result.appState?.returnTo;
      } catch (error) {
        this.cleanUrl(loc);
        clearAuthCache();
        throw error;
      }
      this.cleanUrl(loc);
    }
    const ctx = this.urlContext;
    if (ctx) {
      if (appStateReturnTo && isAllowedReturnTo(appStateReturnTo, ctx)) return appStateReturnTo;
      if (loc) return readReturnTo(loc.search, ctx, fallback);
    }
    return appStateReturnTo ?? fallback;
  }
  /** Read + validate the `returnTo` query param at the current location. */
  getReturnTo(fallback) {
    const loc = this.getLocation();
    if (!loc || !this.urlContext) return fallback;
    return readReturnTo(loc.search, this.urlContext, fallback);
  }
  /** Runs synchronously before identity teardown or navigation begins. */
  subscribeIdentityChange(listener) {
    this.identityListeners.add(listener);
    return () => {
      this.identityListeners.delete(listener);
    };
  }
  /** Stop live identity resources before running the host cache teardown. */
  async tearDownIdentity(reason) {
    for (const listener of this.identityListeners) {
      try {
        listener();
      } catch (error) {
        this.log.warn("identity listener failed", { error });
      }
    }
    if (!this.onIdentityChange) return;
    try {
      await this.onIdentityChange(reason);
    } catch (error) {
      this.log.warn("onIdentityChange failed", { reason, error });
    }
  }
  /**
   * Full sign-out: drop our JWT/token keys from localStorage, then end the Auth0
   * session (`/v2/logout`) and land on {@link LogoutOptions.returnTo}.
   *
   * We {@link clearAuthCache} *before* the Auth0 round-trip so the local token
   * cache is authoritatively wiped here, in one place. auth0-spa-js clears only
   * the *current* client's cache on logout; this also sweeps stale/orphaned
   * `@@auth0spajs@@` entries (from an older clientId/audience/scope), so a
   * returning "Sign in" can never boot on a leftover token. Cheap and non-fatal
   * (no-op off-browser / with storage disabled).
   *
   * {@link SessionManagerOptions.onIdentityChange} runs first, for the state the
   * token cache doesn't cover — decoded transaction bodies in IndexedDB, the
   * signed-CDN session, scoped preferences.
   */
  async logout(options = {}) {
    this.breaker.reset();
    this.log.debug("logout", { returnTo: options.returnTo, federated: options.federated });
    await this.tearDownIdentity("logout");
    clearAuthCache();
    this.qaStore?.clear();
    await this.client.logout({
      logoutParams: { returnTo: options.returnTo, federated: options.federated }
    });
  }
  /**
   * Switch the active tenant within the current org via a silent token refresh
   * (`cacheMode: "off"`), then reload so every consumer re-reads the claims.
   *
   * Teardown runs BEFORE the refresh, while the old tenant is still the current
   * one — that is the identity whose cached data has to go.
   */
  async switchTenant(tenantId, reload) {
    this.log.debug("switchTenant", { tenantId });
    await this.tearDownIdentity("tenant-switch");
    await switchTenant(
      {
        getAccessTokenSilently: (o) => this.client.getTokenSilently(o),
        // Fallback when the silent grant can't carry the authorize-time
        // tenant_id (no same-site iframe fallback) — re-auth via /authorize.
        loginWithRedirect: (o) => this.client.loginWithRedirect(o)
      },
      tenantId,
      { reload }
    );
  }
  /**
   * Re-mint the session when its token predates a plan change.
   *
   * Call it from wherever the LIVE subscription is already being fetched (the
   * billing screen) — there is no webhook and none is needed. The token's
   * scopes were clamped to the plan at mint time, so after an upgrade the UI
   * would keep withholding what the customer just bought until the token
   * expired; after a downgrade it would keep offering what the API now
   * refuses. Self-healing for both, and for a change made in another tab.
   *
   * Returns whether a re-mint was started. Only a genuine, knowable mismatch
   * triggers one:
   *
   * - an ABSENT token claim is "could not tell", never a tier, so it never
   *   counts as a mismatch — treating it as one would re-mint forever for a
   *   customer with no subscription row;
   * - an absent LIVE plan is the same — nothing to compare against;
   * - and each distinct mismatch is re-minted at most ONCE per session, because
   *   the two sides can legitimately disagree for up to a minute (the login's
   *   plan read is cached, `commons.authz.plans`) and a reload loop is a worse
   *   failure than a stale badge. A re-mint that FAILS is not one of those
   *   times: it reloads nothing, so it cannot loop, and it leaves the mismatch
   *   free to be tried again.
   *
   * `staleScopes` is the second kind of mismatch, and the commoner one. An
   * entitlement OVERRIDE withdraws a scope without moving `plan_id`, so the tier
   * comparison above sees nothing while the token keeps a verb the API refuses —
   * pass {@link staleClampedScopes} to catch it. The once-per-mismatch guard
   * matters far more here: if login does not in fact clamp, the re-minted token
   * comes back carrying the same scope, and without a guard that survives the
   * reload this would reload forever.
   *
   * Authority is unaffected either way: the authorizer resolves the plan per
   * request, so a downgrade bites immediately however old the token is.
   */
  async reconcilePlan(livePlanId, reload, staleScopes = []) {
    if (!livePlanId) return false;
    let tokenPlanId;
    try {
      const idClaims = await this.client.getIdTokenClaims() ?? {};
      tokenPlanId = readAutheClaims(idClaims, this.ns).plan_id;
    } catch (error) {
      this.log.warn("could not read the session's plan", { error });
      return false;
    }
    const mismatch = tokenPlanId && tokenPlanId !== livePlanId ? { key: `plan:${tokenPlanId}->${livePlanId}`, reason: { from: tokenPlanId, to: livePlanId } } : staleScopes.length > 0 ? { key: `scopes:${[...staleScopes].sort().join(",")}`, reason: { staleScopes } } : null;
    if (!mismatch || this.hasReminted(mismatch.key)) return false;
    this.claimRemint(mismatch.key);
    this.log.info("session predates a plan change; re-minting", mismatch.reason);
    try {
      await this.client.getTokenSilently({ cacheMode: "off" });
    } catch (error) {
      this.releaseRemint(mismatch.key);
      this.log.warn("could not re-mint the session for the plan change", { error });
      return false;
    }
    await this.tearDownIdentity("tenant-switch");
    (reload ?? (() => this.getLocation()?.reload()))();
    return true;
  }
  /**
   * The re-mint guard, in memory and in the reload-surviving store.
   *
   * Storage is best-effort on purpose. With it blocked the guard degrades to
   * in-memory, which still stops a concurrent double re-mint and still stops a
   * loop within one page life — it just cannot see across the reload. That is a
   * weaker guarantee, not a broken one, and it is the right trade against
   * refusing to re-mint at all because a browser will not give us a key.
   */
  hasReminted(key) {
    if (this.remintedFor.has(key)) return true;
    try {
      return this.getRemintStore()?.getItem(storageKey("session", "reminted", key)) != null;
    } catch {
      return false;
    }
  }
  claimRemint(key) {
    this.remintedFor.add(key);
    try {
      this.getRemintStore()?.setItem(storageKey("session", "reminted", key), "1");
    } catch {
    }
  }
  releaseRemint(key) {
    this.remintedFor.delete(key);
    try {
      this.getRemintStore()?.removeItem(storageKey("session", "reminted", key));
    } catch {
    }
  }
  /** Switch the active Auth0 Organization via a full redirect re-auth. */
  async switchOrganization(organization) {
    this.log.debug("switchOrganization", { organization });
    await this.tearDownIdentity("tenant-switch");
    await switchOrganization(this.client, organization);
  }
  cleanUrl(loc) {
    try {
      const url = new URL(loc.href);
      let changed = false;
      for (const p of CALLBACK_PARAMS) {
        if (url.searchParams.has(p)) {
          url.searchParams.delete(p);
          changed = true;
        }
      }
      if (changed) this.replaceState(url.pathname + url.search + url.hash);
    } catch {
    }
  }
  async toState(session) {
    if (session) {
      this.breaker.recordSuccess();
      return { status: "authenticated", session };
    }
    return { status: "unauthenticated", session: null };
  }
};
function stripUndefined(params) {
  if (!params) return void 0;
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") out[k] = v;
  }
  return Object.keys(out).length ? out : void 0;
}

// src/session/factory.ts
async function createAuth0SpaClient(config, options = {}) {
  let mod;
  try {
    mod = await import('@auth0/auth0-spa-js');
  } catch {
    throw new Error(
      "@auth0/auth0-spa-js is required for createAuth0SpaClient(). Install it, or inject your own client into SessionManager."
    );
  }
  return mod.createAuth0Client({
    domain: config.domain,
    clientId: config.clientId,
    useRefreshTokens: options.useRefreshTokens ?? true,
    useRefreshTokensFallback: options.useRefreshTokensFallback ?? false,
    cacheLocation: options.cacheLocation ?? "localstorage",
    authorizationParams: {
      redirect_uri: config.authorizationParams.redirect_uri,
      audience: config.authorizationParams.audience,
      scope: config.authorizationParams.scope,
      organization: config.authorizationParams.organization
    }
  });
}

// src/session/impersonation-client.ts
var ImpersonationSessionExpired = class extends Error {
  static {
    brandClass(this, "session/ImpersonationSessionExpired");
  }
  constructor() {
    super("This impersonation session has ended. Start a new one from the admin portal.");
    this.name = "ImpersonationSessionExpired";
  }
};
var EXPIRY_LEEWAY_SECONDS = 30;
var ImpersonationClient = class {
  constructor(tokens, options = {}) {
    this.tokens = tokens;
    this.now = options.now ?? (() => Date.now());
    this.onExpired = options.onExpired;
    this.claims = decodeJwtPayload(tokens.id_token) ?? {};
    const accessClaims = decodeJwtPayload(tokens.access_token) ?? {};
    const exp = typeof accessClaims.exp === "number" ? accessClaims.exp : null;
    this.expiresAtMs = exp !== null ? (exp - EXPIRY_LEEWAY_SECONDS) * 1e3 : this.now() + (tokens.expires_in ?? 0) * 1e3;
  }
  tokens;
  static {
    brandClass(this, "session/ImpersonationClient");
  }
  claims;
  expiresAtMs;
  now;
  onExpired;
  revoked = false;
  /** True once the session is over, by the clock or because we ended it. */
  get expired() {
    return this.revoked || this.now() >= this.expiresAtMs;
  }
  async isAuthenticated() {
    return !this.expired;
  }
  async getUser() {
    return this.expired ? void 0 : this.claims;
  }
  async getIdTokenClaims() {
    return this.expired ? void 0 : this.claims;
  }
  async getTokenSilently() {
    if (this.expired) {
      this.onExpired?.();
      throw new ImpersonationSessionExpired();
    }
    return this.tokens.access_token;
  }
  async loginWithRedirect() {
    this.onExpired?.();
    throw new ImpersonationSessionExpired();
  }
  async handleRedirectCallback() {
    return { appState: void 0 };
  }
  async logout() {
    this.revoked = true;
  }
};

// src/session/identity.ts
function identityKey(claims, userId) {
  const segment = (v) => v == null || ["", "main", "*"].includes(String(v).trim()) ? null : String(v).trim();
  return JSON.stringify([
    claims.customer_id,
    claims.org_id,
    claims.tenant_id,
    userId ?? claims.user_id,
    claims.impersonation_id,
    claims.impersonation_access_level
  ].map(segment));
}
function sessionIdentityKey(session) {
  return identityKey({
    ...session.claims,
    customer_id: session.tenant.customerId,
    org_id: session.tenant.orgId,
    tenant_id: session.tenant.tenantId
  }, session.user.id);
}
function tokenMatchesSession(token, session) {
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  const claims = createClaimReader().readAll(payload);
  return identityKey(claims, claims.user_id ?? (typeof payload.sub === "string" ? payload.sub : null)) === sessionIdentityKey(session);
}

// src/session/actor.ts
function actorDisplayName(id, options = {}) {
  const subject = id?.trim();
  if (!subject) return options.missing ?? "Not recorded";
  const friendly = (value) => {
    const label = value?.trim();
    return label && label !== subject ? label : void 0;
  };
  if (options.self?.id === subject) {
    const own = friendly(options.self.name) ?? friendly(options.self.email);
    if (own) return own;
  }
  return friendly(options.names?.get(subject)) ?? subject;
}

// src/session/portal-session.ts
function createPortalSession() {
  let current = null;
  let manager = null;
  const listeners = /* @__PURE__ */ new Set();
  function notify() {
    for (const listener of listeners) listener();
  }
  function adopt(raw) {
    if (!current || current.accessToken === raw) return;
    current = { ...current, accessToken: raw };
    notify();
  }
  return {
    get session() {
      return current;
    },
    set(session) {
      current = session;
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    attach(next) {
      manager = next;
    },
    currentAutheToken() {
      const session = current;
      if (!session) return null;
      const sub = (typeof session.idClaims.sub === "string" ? session.idClaims.sub : void 0) ?? session.user.id ?? "";
      return {
        ...session.idClaims,
        __raw: session.accessToken,
        sub
      };
    },
    async accessTokenFor() {
      if (!manager) return current?.accessToken ?? null;
      const fresh = await manager.getAccessToken();
      if (fresh) adopt(fresh);
      return fresh ?? current?.accessToken ?? null;
    },
    async recoverAuth() {
      if (!manager) return false;
      const fresh = await manager.refreshAccessToken();
      if (fresh) {
        adopt(fresh);
        return true;
      }
      await manager.login();
      return false;
    }
  };
}

export { AuthCircuitBreaker, ImpersonationClient, ImpersonationSessionExpired, QA_OVERRIDE_STORAGE_KEY, SESSION_USER_PARAM, SessionError, SessionManager, actorDisplayName, classifyAuthError, clearAuthCache, createAuth0SpaClient, createPortalSession, hasAuthCallbackParams, localQaOverrideStore, parseDenyReason, qaOverrideFromUrl, readQaOverride, sessionIdentityKey, tokenMatchesSession, validSessionSubject, withQaOverride, withSessionHandoff };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map